import React, {
  useMemo,
  useRef,
  useState,
  useContext,
  useEffect,
  Context,
  ComponentType,
  ReactNode,
} from 'react';

import {
  initialize,
  LDClient,
  LDContext,
  LDOptions,
} from 'launchdarkly-js-client-sdk';

import { getRegion, isGovPartition } from '@adsk/acs-sc-web-platform-api';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import {
  getLocalStorageFlagOverride,
  useOnOverrideChange,
} from './LaunchDarklyLocalStorage';
import {
  getErrorSuffix,
  reportIdentifyUserError,
  reportInitializationError,
  useReportErrorOnce,
  useWarnMissingDefaultOnce,
} from './launchDarklyUtils';
import { useUser } from './ProjectEntitlementsContext';
import {
  useFeatureFlagCore,
  useFeatureFlagOnceCore,
  useAllFeatureFlagsCore,
} from './useFeatureFlagCore';

export type LaunchDarklyContextValue = {
  client: {
    current: LDClient | null;
  };
  loading: boolean;
};

type SimpleLaunchDarklyProviderProps = {
  apiKey: string;
  user?: LDContext;
  options?: LDOptions;
  children?: ReactNode;
};

export type LaunchDarklyProviderProps = {
  apiKey: string;
  projectEntitlementsUnavailable?: boolean;
  children?: ReactNode;
  customUserAttrs?: LDContext['custom'];
  options?: LDOptions;
};

export type UseFeatureFlagOptions = {
  liveStreaming: boolean;
};

export const DEFAULT_ANONYMOUS_USER_ID = 'nonuser' as const;

export const LOADING_TIMEOUT_SECONDS = 5;

const defaultOptions = {
  allAttributesPrivate: true,
  sendEventsOnlyForVariation: true,
  streaming: true,
};

/**
 * This method creates a new LaunchDarkly providers & hooks.
 * We need this method in order to have multiple contexts in a single page (one for Web Platform, one for RFIs, etc.)
 * That's the reason for creating `LaunchDarklyContext` inside this method (or using the provided one), instead of globally.
 * See example for how we generate this in ./AlloyLaunchDarklyProvider.js
 *
 * @returns {{ SimpleLaunchDarklyProvider, LaunchDarklyProvider, useFeatureFlag, useAllFeatureFlags, }}
 */
const createLaunchDarklyContext = (
  context?: Context<LaunchDarklyContextValue | undefined>,
) => {
  const LaunchDarklyContext =
    context ||
    React.createContext<LaunchDarklyContextValue | undefined>(undefined);

  /**
   * Simple Launch Darkly provider without expecting any other provider to exist.
   *
   * @param apiKey
   * @param user
   * @param options
   * @param children
   * @constructor
   */
  const SimpleLaunchDarklyProvider: ComponentType<
    SimpleLaunchDarklyProviderProps
  > = ({ apiKey, user, options, children }) => {
    const client = useRef<LDClient | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      let cancel = false;

      const onError = (
        reportLDError: (error: unknown) => void,
        error: unknown,
      ) => {
        reportLDError(error);
        // Null out the client so hooks fall back to defaultValue.
        // Prevents stale/late SDK responses from changing values after loading is already false.
        client.current = null;
      };

      const initializeClient = async (ldUser: LDContext) => {
        try {
          client.current = initialize(apiKey, ldUser, options);
          await client.current.waitForInitialization(LOADING_TIMEOUT_SECONDS);
        } catch (error) {
          if (cancel) return;
          onError(reportInitializationError, error);
        } finally {
          if (!cancel) setLoading(false);
        }
      };

      const identifyUser = (ldClient: LDClient, ldUser: LDContext) => {
        setLoading(true);
        try {
          // If a callback provided to identify, then promise is not returned
          ldClient.identify(ldUser, undefined, (error?: Error | null) => {
            if (cancel) return;
            if (error) {
              onError(reportIdentifyUserError, error);
            }
            setLoading(false);
          });
        } catch (error) {
          if (cancel) return;
          onError(reportIdentifyUserError, error);
          setLoading(false);
        }
      };

      if (user) {
        if (client.current === null) {
          initializeClient(user);
        } else if (!cancel) {
          identifyUser(client.current, user);
        }
      }
      return () => {
        cancel = true;
      };
    }, [apiKey, user, options]);

    const value = useMemo(() => ({ loading, client }), [loading, client]);
    return (
      <LaunchDarklyContext.Provider value={value}>
        {children}
      </LaunchDarklyContext.Provider>
    );
  };

  /**
   * Launch Darkly Provider that expected to be inside `AlloyProvider` and uses this data to initiate LD calls.
   *
   * @param apiKey
   * @param projectEntitlementsUnavailable
   * @param children
   * @param customUserAttrs
   * @constructor
   */
  const LaunchDarklyProvider: ComponentType<LaunchDarklyProviderProps> = ({
    apiKey,
    projectEntitlementsUnavailable = false,
    children,
    customUserAttrs,
    options = defaultOptions,
  }) => {
    const projectId = useCurrentProjectId();
    const accountId = useCurrentAccountId();
    const { user } = useUser();
    const { analyticsId, email } = user || {};
    const region = getRegion();
    const isAfg = isGovPartition();
    const dataReady =
      region &&
      user &&
      (accountId || projectId || projectEntitlementsUnavailable);
    const ldUser = useMemo(() => {
      if (!dataReady) {
        return undefined;
      }
      const custom = customUserAttrs ?? {};
      if (region) {
        Object.assign(custom, { region });
      }
      if (accountId) {
        Object.assign(custom, { accountId });
      }
      if (projectId) {
        Object.assign(custom, { projectId });
      }
      return {
        kind: 'user',
        key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
        anonymous: true,
        // Afg (GOVCLOUD) compliance forbids sending PII to LaunchDarkly. Omit
        // `email` entirely for Afg; COMMERCIAL behavior unchanged.
        ...(isAfg ? {} : { email }),
        ...custom,
      };
    }, [
      dataReady,
      customUserAttrs,
      region,
      accountId,
      projectId,
      analyticsId,
      email,
      isAfg,
    ]);

    return (
      <SimpleLaunchDarklyProvider
        user={ldUser}
        apiKey={apiKey}
        options={options}
      >
        {children}
      </SimpleLaunchDarklyProvider>
    );
  };

  /**
   *
   * @param flagName
   * @param defaultValue
   * @returns {{loading, value: *}}
   */
  const useFeatureFlag = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const ldContext = useContext(LaunchDarklyContext);
    return useFeatureFlagCore(ldContext, flagName, defaultValue);
  };

  /**
   * Ensures stable feature flag value and loading state without flickering during additional fetches.
   * @param flagName - The name of the feature flag to retrieve
   * @param defaultValue - The default value to use before the flag is loaded
   * @returns Object containing stable loading state and flag value
   */
  const useFeatureFlagOnce = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    const result = useFeatureFlag(flagName, defaultValue);
    return useFeatureFlagOnceCore(result, defaultValue);
  };

  /**
   *
   * @param flagName
   * @param defaultValue
   * @returns {{loading, value: *}}
   */

  const useLiveFeatureFlag = <FlagValue,>(
    flagName: string,
    defaultValue?: FlagValue,
  ) => {
    useWarnMissingDefaultOnce(flagName, defaultValue);
    const reportErrorOnce = useReportErrorOnce();

    const ldContext = useContext(LaunchDarklyContext);
    const [value, setValue] = useState<FlagValue | undefined>();
    const isInitialized = useRef(false);
    const { client, loading: ldLoading } = ldContext || {};

    const finalValue = useMemo(
      () => value ?? defaultValue,
      [defaultValue, value],
    );

    const loading: boolean = ldLoading || !isInitialized.current;

    useOnOverrideChange(() => {
      let lsOverride;
      try {
        lsOverride = getLocalStorageFlagOverride<FlagValue>(flagName);
      } catch (error) {
        console.warn(
          `LaunchDarkly error reading localStorage flag '${flagName}':`,
          error,
        );
      }
      if (lsOverride?.isOverridden) {
        setValue(lsOverride.value);
      } else if (isInitialized.current) {
        // Override cleared — fall back to current LD value
        try {
          setValue(client?.current?.variation(flagName, defaultValue));
        } catch (error) {
          reportErrorOnce(
            `LaunchDarkly error reading flag ${flagName} on override change${getErrorSuffix(error)}`,
            { flagName },
          );
          setValue(defaultValue);
        }
      } else {
        setValue(defaultValue);
      }
    });

    useEffect(() => {
      if (!client?.current) return;

      if (!ldLoading && client.current && !isInitialized.current) {
        let lsOverride;
        try {
          lsOverride = getLocalStorageFlagOverride<FlagValue>(flagName);
          if (lsOverride.isOverridden) {
            setValue(lsOverride.value);
            isInitialized.current = true;
            return;
          }
        } catch (error) {
          console.warn(
            `LaunchDarkly error reading localStorage flag '${flagName}':`,
            error,
          );
        }

        try {
          client.current.on(`change:${flagName}`, (updatedValue: FlagValue) => {
            setValue(updatedValue);
          });
          setValue(client.current.variation(flagName, defaultValue));
        } catch (error) {
          reportErrorOnce(
            `LaunchDarkly error initializing live flag ${flagName}${getErrorSuffix(error)}`,
            { flagName },
          );
        }
        isInitialized.current = true;
      }
    }, [client, defaultValue, flagName, ldLoading, value, reportErrorOnce]);

    return {
      loading: ldContext ? loading : false,
      value: finalValue,
    };
  };

  /**
   *
   * @returns {{loading, value: *}}
   */
  const useAllFeatureFlags = () => {
    const ldContext = useContext(LaunchDarklyContext);
    return useAllFeatureFlagsCore(ldContext);
  };

  return {
    SimpleLaunchDarklyProvider,
    LaunchDarklyProvider,
    useFeatureFlag,
    useFeatureFlagOnce,
    useLiveFeatureFlag,
    useAllFeatureFlags,
  };
};

export default createLaunchDarklyContext;
